import React from "react";
import { css } from "@emotion/react";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import theme from "@styles/theme";
import { Attachment } from "../../../../types/common";
import AttachmentMedia from "./AttachmentMedia";

interface IssueAttachmentsProps {
  attachments?: Attachment[];
}

const AttachmentSlide: React.FC<{
  attachment: Attachment;
  customCss?: ReturnType<typeof css>;
}> = ({ attachment, customCss }) => {
  return <AttachmentMedia attachment={attachment} mediaCss={customCss} />;
};

const IssueAttachments: React.FC<IssueAttachmentsProps> = ({ attachments }) => {
  const { t } = useTranslation();

  if (!attachments?.length) {
    return (
      <div css={[styles.noAttachments, styles.carouselContainer]}>
        <h6 color="rgba(0, 0, 0, 0.4)">{t("issue.noAttachment")}</h6>
      </div>
    );
  }

  if (attachments.length === 1) {
    return (
      <div css={styles.carouselContainer}>
        <AttachmentSlide attachment={attachments[0]} customCss={styles.image} />
      </div>
    );
  }

  return (
    <div css={styles.carouselContainer}>
      <Carousel
        showThumbs={false}
        width={327}
        showStatus={false}
        renderArrowNext={(clickHandler, hasNext) => (
          <div
            onClick={hasNext ? clickHandler : undefined}
            css={styles.arrow}
            style={{
              right: theme.sizing.scale800,
              opacity: hasNext ? 1 : 0.5,
            }}>
            <FiChevronRight css={styles.chevron} />
          </div>
        )}
        renderArrowPrev={(clickHandler, hasNext) => (
          <div
            onClick={hasNext ? clickHandler : undefined}
            css={styles.arrow}
            style={{
              left: theme.sizing.scale800,
              opacity: hasNext ? 1 : 0.5,
            }}>
            <FiChevronLeft css={styles.chevron} />
          </div>
        )}
        renderIndicator={(onClickHandler, isSelected, index, label) => {
          if (isSelected) {
            return (
              <li
                css={[styles.indicator, styles.selected]}
                aria-label={`Selected: ${label} ${index + 1}`}
                title={`Selected: ${label} ${index + 1}`}
              />
            );
          }

          return (
            <li
              css={styles.indicator}
              onClick={onClickHandler}
              onKeyDown={onClickHandler}
              value={index}
              key={index}
              role="button"
              tabIndex={0}
              title={`${label} ${index + 1}`}
              aria-label={`${label} ${index + 1}`}
            />
          );
        }}>
        {attachments.map((attachment) => (
          <AttachmentSlide
            key={attachment.id ?? attachment.url}
            attachment={attachment}
          />
        ))}
      </Carousel>
    </div>
  );
};

const styles = {
  carouselContainer: css({
    marginBottom: theme.sizing.scale800,
  }),
  noAttachments: css({
    display: "flex",
    alignSelf: "stretch",
    alignItems: "center",
    justifyContent: "center",
    height: 48,
    border: "1px dashed rgba(0, 0, 0, 0.4)",
  }),
  image: css({
    width: 327,
    height: 220,
    objectFit: "contain",
    backgroundColor: theme.colors.mono100,
  }),
  chevron: css({
    width: 24,
    height: 24,
    cursor: "pointer",
  }),
  indicator: css({
    background: theme.colors.white,
    width: 8,
    height: 8,
    verticalAlign: "middle",
    borderRadius: theme.borders.radius200,
    display: "inline-block",
    margin: `0 ${theme.sizing.scale200}`,
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.16)",
  }),
  selected: css({
    width: 12,
    height: 12,
    borderRadius: theme.borders.radius300,
  }),
  arrow: css({
    display: "flex",
    position: "absolute",
    zIndex: 2,
    top: "calc(50% - 15px)",
    width: 36,
    height: 36,
    borderRadius: theme.borders.radius500,
    backgroundColor: theme.colors.white,
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  }),
};

export default IssueAttachments;
